/**
 * BillTxnImportTab.jsx — shared upload tab used by both
 * "Import Bill Transactions (Payments)" and "Import Refunds" tabs
 * inside the Super Admin → Dev Tools workspace.
 *
 * Props:
 *   type       'payments' | 'refunds'
 *   title      Tab heading
 *   subtitle   One-line description
 *
 * Backend endpoints:
 *   GET  /api/dev-tools/import/template?type=...
 *   POST /api/dev-tools/import/upload         (multipart: type, file)
 *   GET  /api/dev-tools/import/jobs?type=...
 *   GET  /api/dev-tools/import/jobs/{job_id}
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Upload, Loader2, Download, FileSpreadsheet, CheckCircle2, AlertTriangle,
  XCircle, Clock, Link as LinkIcon, RefreshCw, Coffee, MoreVertical, Trash2, RotateCcw,
} from 'lucide-react';

const fmtD = (d) => {
  if (!d) return '\u2014';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
};

const StatusPill = ({ status }) => {
  const map = {
    processing: { bg: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2, label: 'Processing', spin: true },
    completed: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Completed' },
    failed: { bg: 'bg-red-50 text-red-700 border-red-200', icon: XCircle, label: 'Failed' },
  };
  const cfg = map[status] || { bg: 'bg-gray-50 text-gray-700 border-gray-200', icon: Clock, label: status || 'Queued' };
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded ${cfg.bg}`}
      data-testid={`status-pill-${status}`}
    >
      <Icon className={`h-3 w-3 ${cfg.spin ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
};

export default function BillTxnImportTab({ type, title, subtitle }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asyncJobId, setAsyncJobId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // job obj or null
  const [busyJobId, setBusyJobId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dev-tools/import/jobs?type=${type}&limit=50`);
      setJobs(data.jobs || []);
    } catch (e) {
      toast.error('Failed to load upload history');
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll async job until completion
  useEffect(() => {
    if (!asyncJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/dev-tools/import/jobs/${asyncJobId}`);
        if (cancelled) return;
        if (data.status === 'completed') {
          toast.success(
            `Import finished: ${data.success_count} success · ${data.warning_count} skipped · ${data.error_count} errors`,
          );
          setAsyncJobId(null);
          fetchJobs();
          return;
        }
        if (data.status === 'failed') {
          toast.error(`Import failed: ${data.error_message || 'Unknown error'}`);
          setAsyncJobId(null);
          fetchJobs();
          return;
        }
        // still processing → refresh history then re-poll
        fetchJobs();
        setTimeout(tick, 4000);
      } catch {
        if (!cancelled) setTimeout(tick, 6000);
      }
    };
    const t = setTimeout(tick, 3500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [asyncJobId, fetchJobs]);

  const onFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xls|xlsx)$/i.test(f.name)) {
      toast.error('Only .xls or .xlsx files are allowed');
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error('File exceeds 25 MB limit');
      return;
    }
    setFile(f);
  };

  const onDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/dev-tools/import/template?type=${type}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Template download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dev_tools_${type}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const onUpload = async () => {
    if (!file) {
      toast.error('Pick a file first');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('type', type);
    fd.append('file', file);
    try {
      const { data } = await api.post('/dev-tools/import/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // long timeout for sync runs near the 200-row threshold
        timeout: 120000,
      });
      if (data.mode === 'sync') {
        const j = data.job || {};
        toast.success(
          `Imported ${j.row_count || 0} rows · ${j.success_count || 0} ✓  ·  ${j.warning_count || 0} ⚠  ·  ${j.error_count || 0} ✗`,
        );
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchJobs();
      } else {
        toast.message('Large file detected', {
          description: `Processing ${data.row_count} rows in background. Grab a coffee ☕ — we'll auto-refresh the table when it's done.`,
        });
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setAsyncJobId(data.job_id);
        fetchJobs();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDeleteJob = async () => {
    if (!pendingDelete) return;
    const job = pendingDelete;
    setBusyJobId(job.job_id);
    try {
      const { data } = await api.delete(`/dev-tools/import/jobs/${job.job_id}`);
      toast.success(
        `Deleted job · reverted ${data.reverted_rows ?? 0} transaction(s)` +
          (data.failed_rows ? ` · ${data.failed_rows} failed to revert` : ''),
      );
      setPendingDelete(null);
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    } finally {
      setBusyJobId(null);
    }
  };

  const onReprocessJob = async (job) => {
    setBusyJobId(job.job_id);
    try {
      const { data } = await api.post(`/dev-tools/import/jobs/${job.job_id}/reprocess`, {}, { timeout: 120000 });
      if (data.mode === 'sync') {
        const j = data.job || {};
        toast.success(
          `Reprocessed ${j.row_count || 0} rows · ${j.success_count || 0} ✓ · ${j.warning_count || 0} ⚠ · ${j.error_count || 0} ✗`,
        );
      } else {
        toast.message('Reprocess started', {
          description: `Re-running ${data.row_count} rows in background. The table will refresh when it's done.`,
        });
        setAsyncJobId(data.job_id);
      }
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reprocess job');
    } finally {
      setBusyJobId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid={`bill-txn-import-tab-${type}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onDownloadTemplate}
          disabled={downloadingTemplate}
          data-testid={`download-template-${type}`}
        >
          {downloadingTemplate ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download Template
        </Button>
      </div>

      {/* Upload form */}
      <div className="border rounded-lg p-5 bg-card" data-testid={`upload-form-${type}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">
              {type === 'payments' ? 'Payments File (.xls / .xlsx)' : 'Refunds File (.xls / .xlsx)'} *
            </label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              onChange={onFileSelect}
              className="h-9 text-sm"
              disabled={uploading}
              data-testid={`upload-file-input-${type}`}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={onUpload}
              disabled={!file || uploading}
              className="w-full"
              data-testid={`upload-submit-${type}`}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading… please wait
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload &amp; Process
                </>
              )}
            </Button>
          </div>
        </div>
        {file && (
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            <span className="font-mono">{file.name}</span>
            <span>({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
        )}
        {uploading && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            <Coffee className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Hold tight</strong> — large files run in the background. You can keep working on other tabs; we'll
              auto-refresh this list and pop a toast when it's done. No need to click upload again.
            </div>
          </div>
        )}
        {asyncJobId && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid={`async-banner-${type}`}>
            <Coffee className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Processing in background</strong> — feel free to grab a coffee ☕. We'll auto-update this list
              and notify you when the import completes. Job ID: <span className="font-mono">{asyncJobId.slice(0, 8)}…</span>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="border rounded-lg bg-card">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold">Past Uploads</h3>
          <button
            onClick={fetchJobs}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-border rounded hover:bg-muted transition-colors"
            data-testid={`refresh-history-${type}`}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid={`jobs-table-${type}`}>
            <thead className="bg-muted/30 border-b">
              <tr>
                <Th>File</Th>
                <Th>Status</Th>
                <Th align="right">Rows</Th>
                <Th align="right">Success</Th>
                <Th align="right">Warnings</Th>
                <Th align="right">Errors</Th>
                <Th>Uploaded By</Th>
                <Th>Uploaded</Th>
                <Th align="center">Original</Th>
                <Th align="center">Output</Th>
                <Th align="center">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}><td colSpan={11} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td></tr>
                ))
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-muted-foreground">
                    <FileSpreadsheet className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>No uploads yet — pick a file above to get started.</p>
                  </td>
                </tr>
              ) : (
                jobs.map((j, i) => (
                  <tr key={j.job_id} className="border-b border-border/40 hover:bg-muted/30" data-testid={`job-row-${type}-${i}`}>
                    <td className="px-3 py-2 font-mono text-[11px] truncate max-w-[220px]" title={j.file_name}>
                      {j.file_name}
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill status={j.status} />
                      {j.error_message && (
                        <div className="text-[10px] text-red-600 mt-0.5" title={j.error_message}>
                          {String(j.error_message).slice(0, 80)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{(j.row_count ?? 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                      {(j.success_count ?? 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                      {(j.warning_count ?? 0) > 0 ? (
                        <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700">
                          <AlertTriangle className="h-3 w-3" />
                          {j.warning_count}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {(j.error_count ?? 0) > 0 ? (
                        <Badge variant="destructive" className="gap-1">
                          <XCircle className="h-3 w-3" />
                          {j.error_count}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px]">{j.uploaded_by_name || '\u2014'}</td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">{fmtD(j.created_at)}</td>
                    <td className="px-3 py-2 text-center">
                      {j.original_url ? (
                        <a
                          href={j.original_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`original-link-${type}-${i}`}
                        >
                          <LinkIcon className="h-3 w-3" />
                          File
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {j.output_url ? (
                        <a
                          href={j.output_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`output-link-${type}-${i}`}
                        >
                          <Download className="h-3 w-3" />
                          Output
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-muted disabled:opacity-50"
                            disabled={busyJobId === j.job_id || j.status === 'processing'}
                            data-testid={`row-actions-${type}-${i}`}
                            aria-label="Row actions"
                          >
                            {busyJobId === j.job_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreVertical className="h-4 w-4" />
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem
                            disabled={!j.original_s3_key && !j.original_url}
                            onClick={() => onReprocessJob(j)}
                            data-testid={`row-reprocess-${type}-${i}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reprocess
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-700 focus:bg-red-50"
                            onClick={() => setPendingDelete(j)}
                            data-testid={`row-delete-${type}-${i}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
      >
        <AlertDialogContent data-testid={`delete-confirm-${type}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this upload?</AlertDialogTitle>
            <AlertDialogDescription>
              All the {type === 'payments' ? 'transactions' : 'refunds'} recorded for this file
              will be deleted and <strong>cannot be recovered</strong>. Are you sure you want to proceed?
              {pendingDelete?.file_name && (
                <div className="mt-2 text-xs font-mono text-muted-foreground break-all">
                  {pendingDelete.file_name}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-cancel-${type}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteJob}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid={`delete-confirm-yes-${type}`}
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Th = ({ children, align = 'left' }) => (
  <th
    className={`px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground text-${align}`}
  >
    {children}
  </th>
);
